import { ChevronDownIcon, ChevronUpIcon, LockClosedIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { BUSINESS_MENU } from "./menu";
import { hasFeature } from "../services/features";
import { can } from "../services/permissions";

/** Groups marked `defaultOpen` start expanded. */
const initialOpenMenus = () =>
  BUSINESS_MENU.filter((item) => item.subMenu && item.defaultOpen).map((item) => item.key);

export default function Sidebar({ collapsed, user }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  const [openMenus, setOpenMenus] = useState(initialOpenMenus);
  const location = useLocation();
  const navigate = useNavigate();

  const isCollapsed = collapsed && !hovered;
  const business = user?.business;

  // Entries the user's role cannot reach are hidden rather than shown and
  // refused. A group with nothing left in it goes too.
  const visible = (item) => !item.permission || can(user, item.permission);

  // A group whose plan feature is off becomes an upgrade hint — shown only to
  // somebody who could otherwise use something in it.
  const menu = BUSINESS_MENU.filter(visible)
    .map((item) => (item.subMenu ? { ...item, subMenu: item.subMenu.filter(visible) } : item))
    .filter((item) => !item.subMenu || item.subMenu.length > 0)
    .map((item) => (item.feature && !hasFeature(user, item.feature) ? { ...item, locked: true } : item));

  const matches = (item) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const isActive = (item) => (item.subMenu ? item.subMenu.some(matches) : matches(item));

  const toggleMenu = (key) =>
    setOpenMenus((open) => (open.includes(key) ? open.filter((k) => k !== key) : [...open, key]));

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`fixed inset-y-0 start-0 z-40 flex flex-shrink-0 flex-col border-line bg-surface transition-all duration-300 border-e md:relative md:translate-x-0 ${
        isCollapsed ? "w-20" : "w-72"
      } ${collapsed ? "-translate-x-full rtl:translate-x-full md:translate-x-0" : "translate-x-0"}`}
    >
      {/* Product brand — symbol only, 32px, never recoloured. */}
      <div
        className={`flex flex-shrink-0 items-center gap-2 border-b border-line px-4 py-3 ${
          isCollapsed ? "justify-center" : ""
        }`}
      >
        <img
          src={`${process.env.PUBLIC_URL}/img/puralance_mark_256.png`}
          alt="Puralance"
          width={36}
          height={32}
          className="h-8 w-9 flex-shrink-0"
        />
        {!isCollapsed && <span className="truncate text-lg font-bold text-ink">Puralance</span>}
      </div>

      {/* Business identity */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-line px-4 py-4">
        {business?.logo ? (
          <img src={business.logo} alt="" className="h-10 w-10 flex-shrink-0 rounded-lg object-cover" />
        ) : (
          <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg bg-accent text-dense font-bold text-accent-on">
            {business?.name?.[0]?.toUpperCase() ?? "B"}
          </div>
        )}
        {!isCollapsed && (
          <span className="truncate text-lg font-semibold text-ink">
            {business?.name ?? t("app.business")}
          </span>
        )}
      </div>

      {/* Menu */}
      <nav className="mx-1 mb-4 mt-4 flex-1 space-y-1 overflow-y-auto px-2">
        {menu.map((item) => {
          if (item.locked) {
            return (
              <div
                key={item.key}
                title={t("loyalty.upgradeHint")}
                className={`flex min-h-touch items-center rounded-lg px-3 py-3 text-ink-2 ${
                  isCollapsed ? "justify-center" : "justify-between"
                }`}
              >
                <div className="flex items-center">
                  <item.icon className={`${isCollapsed ? "me-0" : "me-1"} h-[21px] w-[21px] stroke-2`} />
                  {!isCollapsed && <span className="font-semibold">{t(item.title)}</span>}
                </div>
                {!isCollapsed && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-raised px-2 py-0.5 text-xs">
                    <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
                    {t("loyalty.upgradeBadge")}
                  </span>
                )}
              </div>
            );
          }

          const active = isActive(item);
          const open = openMenus.includes(item.key);

          return (
            <div key={item.key}>
              <div
                onClick={() => (item.subMenu ? toggleMenu(item.key) : navigate(item.path))}
                className={`relative flex min-h-touch cursor-pointer items-center rounded-lg px-3 py-3
                  transition-colors duration-fast hover:bg-raised ${
                  isCollapsed ? "justify-center" : "justify-between"
                } ${
                  // Highlight only the section the route is in; the chevron alone
                  // conveys whether a group is expanded.
                  active ? "bg-accent-wash font-bold text-accent-text" : "font-semibold text-ink"
                }`}
              >
                {/* The 3px marker sits on the leading edge, so it moves to the
                    right in RTL without a second rule. */}
                {active && (
                  <span className="absolute inset-y-2 start-0 w-[3px] rounded-e bg-accent" aria-hidden="true" />
                )}

                <div className="flex items-center">
                  <item.icon className={`${isCollapsed ? "me-0" : "me-1"} h-[21px] w-[21px] stroke-2`} />
                  {!isCollapsed && <span>{t(item.title)}</span>}
                </div>

                {!isCollapsed && item.subMenu && (
                  <span>
                    {open ? (
                      <ChevronUpIcon className="h-3 w-3 stroke-[3]" />
                    ) : (
                      <ChevronDownIcon className="h-3 w-3 stroke-[3]" />
                    )}
                  </span>
                )}
              </div>

              {/* Submenu — hidden while the rail is collapsed to icons. */}
              {item.subMenu && open && !isCollapsed && (
                <div className="ms-2 mt-1">
                  {item.subMenu.map((sub) => (
                    <div
                      key={sub.key}
                      onClick={() => navigate(sub.path)}
                      className={`relative flex min-h-touch cursor-pointer items-center rounded-md px-4 py-2
                        transition-colors duration-fast hover:bg-raised ${
                        matches(sub) ? "bg-accent-wash font-bold text-accent-text" : "text-ink"
                      }`}
                    >
                      {matches(sub) && (
                        <span className="absolute start-0 h-6 w-[3px] rounded-e bg-accent" aria-hidden="true" />
                      )}
                      <span>{t(sub.title)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
